import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowDownTrayIcon, ExclamationTriangleIcon, ClockIcon, ShieldExclamationIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import UnifiedPulseReportTable from './components/UnifiedPulseReportTable';
import { TicketType, PulseReportChartFilters } from './types/pulseReport';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useDashboardTracking } from '../../hooks/useDashboardTracking';
import DateTimePickerInput from '../../components/DateTimePickerInput';

// Utils
import { DurationPreset, durationPresets, convertPresetToTimeRange, getDurationPresetRange } from './utils/dateHelpers';

interface Customer {
  id: string;
  accountName: string;
}

interface Group {
  id: string;
  name: string;
}

interface SlaChartFilters extends PulseReportChartFilters {
  slaType?: string;
  slaFilterType?: string;
}

const SlaMonitoringDashboard: React.FC = () => {
  // Subscribe to real-time updates via SSE for SLA-tracked ticket types
  useTicketEvents({ ticketTypes: ['INCIDENT', 'SERVICE_REQUEST'] });

  // Get date formatter hook
  const { formatTime } = useDateFormatter();

  // Filter state
  const [customerId, setCustomerId] = useState<string>('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('this_month');
  const [previousPreset, setPreviousPreset] = useState<DurationPreset>('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');

  // Chart interaction state
  const [tableVisible, setTableVisible] = useState(false);
  const [tableFilterLabel, setTableFilterLabel] = useState('');
  const [chartFilters, setChartFilters] = useState<SlaChartFilters>({});

  // Toggle to show all SLA types for Case (Resolution and Ongoing in addition to Response)
  const [showAllCaseSlaTypes, setShowAllCaseSlaTypes] = useState(false);

  // Handle duration preset change
  const handleDurationChange = (newPreset: DurationPreset) => {
    if (newPreset === 'custom') {
      const range = getDurationPresetRange(previousPreset);
      setCustomDateFrom(range.from);
      setCustomDateTo(range.to);
    } else {
      setPreviousPreset(newPreset);
      setCustomDateFrom('');
      setCustomDateTo('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
    }
    setDurationPreset(newPreset);
  };

  const timeRange = useMemo(() => {
    if (durationPreset === 'custom') {
      if (appliedDateFrom && appliedDateTo) {
        return convertPresetToTimeRange('custom', appliedDateFrom, appliedDateTo);
      }
      return convertPresetToTimeRange(previousPreset);
    }
    return convertPresetToTimeRange(durationPreset);
  }, [durationPreset, appliedDateFrom, appliedDateTo, previousPreset]);

  const handleApplyCustomDates = () => {
    if (customDateFrom && customDateTo) {
      setAppliedDateFrom(customDateFrom);
      setAppliedDateTo(customDateTo);
    }
  };

  const customDatesNeedApply = durationPreset === 'custom' &&
    customDateFrom && customDateTo &&
    (customDateFrom !== appliedDateFrom || customDateTo !== appliedDateTo);

  // Track dashboard filters for Application Insights
  useDashboardTracking('SlaMonitoring', {
    customerId,
    durationPreset,
    assignmentGroupId,
    customDateFrom: appliedDateFrom || undefined,
    customDateTo: appliedDateTo || undefined,
  });

  const timezoneOffset = new Date().getTimezoneOffset();

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || response.data || [];
    },
  });

  // Fetch all assignment groups for filtering
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['all-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment&limit=500');
      return response.data.groups || response.data || [];
    },
  });

  // Fetch dashboard data with fresh data on every mount/tab focus
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['sla-monitoring-dashboard', customerId, timeRange, assignmentGroupId, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupId) params.append('assignmentGroupId', assignmentGroupId);
      params.append('timezoneOffset', String(timezoneOffset));

      const response = await api.get(`/dashboard/sla-monitoring?${params}`);
      return response.data;
    },
    staleTime: 0,               // Always fetch fresh data on mount
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always',   // Always refetch on component mount
  });

  // Click handlers
  const handleTotalClick = () => {
    setTableFilterLabel('All SLA Tracked Tickets');
    setChartFilters({ filterType: 'slaAll' });
    setTableVisible(true);
  };

  const handleMetClick = () => {
    setTableFilterLabel('SLA Met Tickets');
    setChartFilters({ filterType: 'slaMet' });
    setTableVisible(true);
  };

  const handleBreachedClick = () => {
    setTableFilterLabel('SLA Breached Tickets');
    setChartFilters({ filterType: 'slaBreached' });
    setTableVisible(true);
  };

  const handleOngoingClick = () => {
    setTableFilterLabel('Ongoing SLA Tickets');
    setChartFilters({ filterType: 'slaOngoing' });
    setTableVisible(true);
  };

  const handleResponseBreachedClick = () => {
    setTableFilterLabel('Response SLA Breached');
    setChartFilters({ filterType: 'slaResponseBreached' });
    setTableVisible(true);
  };

  const handleResolutionBreachedClick = () => {
    setTableFilterLabel('Resolution SLA Breached');
    setChartFilters({ filterType: 'slaResolutionBreached' });
    setTableVisible(true);
  };

  const handleOngoingBreachedClick = () => {
    setTableFilterLabel('Ongoing SLA Breached');
    setChartFilters({ filterType: 'slaOngoingBreached' });
    setTableVisible(true);
  };

  const handleWarningClick = () => {
    setTableFilterLabel('SLA Warning (10-75% elapsed)');
    setChartFilters({ filterType: 'slaWarning' });
    setTableVisible(true);
  };

  const handleAtRiskClick = () => {
    setTableFilterLabel('SLA At Risk (76-99% elapsed)');
    setChartFilters({ filterType: 'slaAtRisk' });
    setTableVisible(true);
  };

  const handlePausedClick = () => {
    setTableFilterLabel('SLA Paused Tickets');
    setChartFilters({ filterType: 'slaPaused' });
    setTableVisible(true);
  };

  const handleCancelledClick = () => {
    setTableFilterLabel('SLA Cancelled Tickets');
    setChartFilters({ filterType: 'slaCancelled' });
    setTableVisible(true);
  };

  const handleTicketTypeSlaClick = (ticketType: string, slaType: string, slaFilterType: string) => {
    const ticketLabels: Record<string, string> = {
      INCIDENT: 'Incidents',
      SERVICE_REQUEST: 'Service Requests',
      CASE: 'Cases'
    };
    const ticketLabel = ticketLabels[ticketType] || ticketType;
    const slaLabel = slaType === 'RESPONSE' ? 'Response' : slaType === 'ONGOING' ? 'Ongoing' : 'Resolution';
    const filterLabels: Record<string, string> = {
      met: 'Met',
      breached: 'Breached',
      ongoing: 'Ongoing',
      paused: 'Paused',
      cancelled: 'Cancelled',
      all: 'All'
    };
    const filterLabel = filterLabels[slaFilterType] || slaFilterType;
    setTableFilterLabel(`${ticketLabel} - ${slaLabel} SLA ${filterLabel}`);
    setChartFilters({
      ticketType: ticketType as TicketType,
      slaType,
      filterType: slaFilterType === 'all' ? 'slaAll' : `sla${slaFilterType.charAt(0).toUpperCase() + slaFilterType.slice(1)}`,
      filterValue: slaType
    });
    setTableVisible(true);
  };

  // Handler for ticket-type specific status tiles (Warning, At Risk, Paused, Cancelled)
  const handleTicketTypeStatusClick = (ticketType: string, statusType: string) => {
    const ticketLabels: Record<string, string> = {
      INCIDENT: 'Incidents',
      SERVICE_REQUEST: 'Service Requests',
      CASE: 'Cases'
    };
    const ticketLabel = ticketLabels[ticketType] || ticketType;
    const statusLabels: Record<string, string> = {
      warning: 'Warning (10-75% elapsed)',
      atRisk: 'At Risk (76-99% elapsed)',
      paused: 'Paused',
      cancelled: 'Cancelled'
    };
    const statusLabel = statusLabels[statusType] || statusType;
    setTableFilterLabel(`${ticketLabel} - ${statusLabel}`);
    setChartFilters({
      ticketType: ticketType as TicketType,
      filterType: `sla${statusType.charAt(0).toUpperCase() + statusType.slice(1)}`
    });
    setTableVisible(true);
  };

  const closeTable = () => {
    setTableVisible(false);
    setChartFilters({});
  };

  const handleExportPDF = async () => {
    if (!dashboardData) return;
    console.log('Export PDF functionality to be implemented');
  };

  const selectedCustomerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName}
                </option>
              ))}
            </select>
          </div>

          {/* Duration Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={durationPreset}
              onChange={(e) => handleDurationChange(e.target.value as DurationPreset)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {durationPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group</label>
            <select
              value={assignmentGroupId}
              onChange={(e) => setAssignmentGroupId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export PDF Button and Refresh */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!dashboardData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Last Updated Indicator */}
        {dataUpdatedAt && (
          <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
            {isFetching && (
              <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            )}
            <span>
              Last updated: {formatTime(new Date(dataUpdatedAt))}
            </span>
          </div>
        )}

        {/* Custom Date Range */}
        {durationPreset === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <DateTimePickerInput
                value={customDateFrom}
                onChange={(val) => setCustomDateFrom(val)}
                showTimeSelect={true}
                className="w-full"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DateTimePickerInput
                  value={customDateTo}
                  onChange={(val) => setCustomDateTo(val)}
                  showTimeSelect={true}
                  className="w-full"
                />
              </div>
              <button
                onClick={handleApplyCustomDates}
                disabled={!customDateFrom || !customDateTo}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  customDatesNeedApply
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading SLA data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load SLA data. Please try again.
          <button onClick={() => refetch()} className="ml-4 text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !isLoading && (
        <>
          {/* Summary Tiles - Row 1: Main Status Counts */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleTotalClick}
            >
              <div className="text-2xl font-bold text-gray-900">{dashboardData.summary?.totalSlaInstances || 0}</div>
              <div className="text-sm text-gray-500">Total SLA Tracked</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleMetClick}
            >
              <div className="text-2xl font-bold text-green-600">{dashboardData.summary?.totalMet || 0}</div>
              <div className="text-sm text-gray-500">SLA Met</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleBreachedClick}
            >
              <div className="text-2xl font-bold text-red-600">{dashboardData.summary?.totalBreached || 0}</div>
              <div className="text-sm text-gray-500">SLA Breached</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleOngoingClick}
            >
              <div className="text-2xl font-bold text-blue-600">{dashboardData.summary?.totalOngoing || 0}</div>
              <div className="text-sm text-gray-500">Ongoing</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handlePausedClick}
            >
              <div className="text-2xl font-bold text-indigo-600">{dashboardData.summary?.totalPaused || 0}</div>
              <div className="text-sm text-gray-500">Paused</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleCancelledClick}
            >
              <div className="text-2xl font-bold text-gray-500">{dashboardData.summary?.totalCancelled || 0}</div>
              <div className="text-sm text-gray-500">Cancelled</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-purple-600">{dashboardData.summary?.overallCompliance || 0}%</div>
              <div className="text-sm text-gray-500">Compliance</div>
            </div>
          </div>

          {/* Summary Tiles - Row 2: Risk Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleWarningClick}
            >
              <div className="text-2xl font-bold text-yellow-600">{dashboardData.summary?.warningCount || 0}</div>
              <div className="text-sm text-gray-500">Warning (10-75% elapsed)</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleAtRiskClick}
            >
              <div className="text-2xl font-bold text-orange-600">{dashboardData.summary?.atRiskCount || 0}</div>
              <div className="text-sm text-gray-500">At Risk (76-99% elapsed)</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleResponseBreachedClick}
            >
              <div className="text-2xl font-bold text-red-600">{dashboardData.riskBars?.responseBreached?.total || 0}</div>
              <div className="text-sm text-gray-500">Response SLA Breached</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleResolutionBreachedClick}
            >
              <div className="text-2xl font-bold text-red-500">{dashboardData.riskBars?.resolutionBreached?.total || 0}</div>
              <div className="text-sm text-gray-500">Resolution SLA Breached</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={handleOngoingBreachedClick}
            >
              <div className="text-2xl font-bold text-red-400">{dashboardData.riskBars?.ongoingBreached?.total || 0}</div>
              <div className="text-sm text-gray-500">Ongoing SLA Breached</div>
            </div>
          </div>

          {/* Risk Bars */}
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {/* Response SLA Breached Bar */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-red-500"
              onClick={handleResponseBreachedClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-red-600">{dashboardData.riskBars?.responseBreached?.total || 0}</div>
                  <div className="text-sm text-gray-500">Response SLA Breached</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.responseBreached?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.responseBreached?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.responseBreached?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* Resolution SLA Breached Bar */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-red-400"
              onClick={handleResolutionBreachedClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ShieldExclamationIcon className="w-6 h-6 text-red-500" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-red-500">{dashboardData.riskBars?.resolutionBreached?.total || 0}</div>
                  <div className="text-sm text-gray-500">Resolution SLA Breached</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.resolutionBreached?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.resolutionBreached?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.resolutionBreached?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* Ongoing SLA Breached Bar */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-red-300"
              onClick={handleOngoingBreachedClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ShieldExclamationIcon className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-red-400">{dashboardData.riskBars?.ongoingBreached?.total || 0}</div>
                  <div className="text-sm text-gray-500">Ongoing SLA Breached</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.ongoingBreached?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.ongoingBreached?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.ongoingBreached?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* Warning Bar (10-75%) */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-yellow-500"
              onClick={handleWarningClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ClockIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-yellow-600">{dashboardData.riskBars?.warning?.total || 0}</div>
                  <div className="text-sm text-gray-500">Warning (10-75%)</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.warning?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.warning?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.warning?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* At Risk Bar (76-99%) */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-orange-500"
              onClick={handleAtRiskClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-orange-600">{dashboardData.riskBars?.atRisk?.total || 0}</div>
                  <div className="text-sm text-gray-500">At Risk (76-99%)</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.atRisk?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.atRisk?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.atRisk?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* Paused Bar */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-indigo-500"
              onClick={handlePausedClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <ClockIcon className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-indigo-600">{dashboardData.riskBars?.paused?.total || 0}</div>
                  <div className="text-sm text-gray-500">SLA Paused</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.paused?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.paused?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.paused?.serviceRequests || 0}</span>
              </div>
            </div>

            {/* Cancelled Bar */}
            <div
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-gray-400"
              onClick={handleCancelledClick}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <ClockIcon className="w-6 h-6 text-gray-500" />
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-gray-500">{dashboardData.riskBars?.cancelled?.total || 0}</div>
                  <div className="text-sm text-gray-500">SLA Cancelled</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                <span>CASE: {dashboardData.riskBars?.cancelled?.cases || 0}</span>
                <span>INC: {dashboardData.riskBars?.cancelled?.incidents || 0}</span>
                <span>SR: {dashboardData.riskBars?.cancelled?.serviceRequests || 0}</span>
              </div>
            </div>
          </div>

          {/* Ticket Type-wise SLA Data */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Case SLA Card - Full Width with 3 SLA Types (Resolution and Ongoing can be toggled) */}
            <div className="bg-white rounded-lg shadow xl:col-span-3">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Case SLA</h3>
                  <button
                    onClick={() => setShowAllCaseSlaTypes(!showAllCaseSlaTypes)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {showAllCaseSlaTypes ? 'Hide' : 'Show'} Resolution & Ongoing
                    <svg className={`w-4 h-4 transition-transform ${showAllCaseSlaTypes ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Unique Ticket Summary - avoids double-counting tickets with multiple SLA types */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 font-medium">Unique Tickets:</span>
                  <div className="flex gap-4">
                    <span className="text-gray-700">Total: <span className="font-bold">{dashboardData.byTicketType?.case?.totalTickets || 0}</span></span>
                    <span className="text-green-600">Met: <span className="font-bold">{dashboardData.byTicketType?.case?.metTickets || 0}</span></span>
                    <span className="text-red-600">Breached: <span className="font-bold">{dashboardData.byTicketType?.case?.breachedTickets || 0}</span></span>
                    <span className="text-blue-600">Ongoing: <span className="font-bold">{dashboardData.byTicketType?.case?.ongoingTickets || 0}</span></span>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4">
                {/* Response SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Response SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.case?.response?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.case?.response?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.case?.response?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.case?.response?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.case?.response?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.case?.response?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.case?.response?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.case?.response?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESPONSE', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.case?.response?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>

                {/* Resolution SLA - Collapsible */}
                {showAllCaseSlaTypes && (
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-700">Resolution SLA</span>
                      <span className="text-sm text-gray-500">
                        Compliance: {dashboardData.byTicketType?.case?.resolution?.compliance || 0}%
                      </span>
                    </div>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                      <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'all')}>
                        <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.case?.resolution?.total || 0}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'met')}>
                        <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.case?.resolution?.met || 0}</div>
                        <div className="text-xs text-gray-500">Met</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'breached')}>
                        <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.case?.resolution?.breached || 0}</div>
                        <div className="text-xs text-gray-500">Breached</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'ongoing')}>
                        <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.case?.resolution?.ongoing || 0}</div>
                        <div className="text-xs text-gray-500">In Progress</div>
                      </div>
                      <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'paused')}>
                        <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.case?.resolution?.paused || 0}</div>
                        <div className="text-xs text-gray-500">Paused</div>
                      </div>
                      <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'cancelled')}>
                        <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.case?.resolution?.cancelled || 0}</div>
                        <div className="text-xs text-gray-500">Cancelled</div>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'warning')}>
                        <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.case?.resolution?.warning || 0}</div>
                        <div className="text-xs text-gray-500">Warning</div>
                      </div>
                      <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('CASE', 'RESOLUTION', 'atRisk')}>
                        <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.case?.resolution?.atRisk || 0}</div>
                        <div className="text-xs text-gray-500">At Risk</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ongoing SLA - Collapsible */}
                {showAllCaseSlaTypes && (
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-700">Ongoing SLA</span>
                      <span className="text-sm text-gray-500">
                        Compliance: {dashboardData.byTicketType?.case?.ongoingSla?.compliance || 0}%
                      </span>
                    </div>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                      <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'all')}>
                        <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.case?.ongoingSla?.total || 0}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'met')}>
                        <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.case?.ongoingSla?.met || 0}</div>
                        <div className="text-xs text-gray-500">Met</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'breached')}>
                        <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.case?.ongoingSla?.breached || 0}</div>
                        <div className="text-xs text-gray-500">Breached</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'ongoing')}>
                        <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.case?.ongoingSla?.ongoing || 0}</div>
                        <div className="text-xs text-gray-500">In Progress</div>
                      </div>
                      <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'paused')}>
                        <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.case?.ongoingSla?.paused || 0}</div>
                        <div className="text-xs text-gray-500">Paused</div>
                      </div>
                      <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'cancelled')}>
                        <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.case?.ongoingSla?.cancelled || 0}</div>
                        <div className="text-xs text-gray-500">Cancelled</div>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'warning')}>
                        <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.case?.ongoingSla?.warning || 0}</div>
                        <div className="text-xs text-gray-500">Warning</div>
                      </div>
                      <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('CASE', 'ONGOING', 'atRisk')}>
                        <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.case?.ongoingSla?.atRisk || 0}</div>
                        <div className="text-xs text-gray-500">At Risk</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Incident SLA Card - Full Width with 3 SLA Types */}
            <div className="bg-white rounded-lg shadow xl:col-span-3">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Incident SLA</h3>
              </div>
              {/* Unique Ticket Summary - avoids double-counting tickets with multiple SLA types */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 font-medium">Unique Tickets:</span>
                  <div className="flex gap-4">
                    <span className="text-gray-700">Total: <span className="font-bold">{dashboardData.byTicketType?.incident?.totalTickets || 0}</span></span>
                    <span className="text-green-600">Met: <span className="font-bold">{dashboardData.byTicketType?.incident?.metTickets || 0}</span></span>
                    <span className="text-red-600">Breached: <span className="font-bold">{dashboardData.byTicketType?.incident?.breachedTickets || 0}</span></span>
                    <span className="text-blue-600">Ongoing: <span className="font-bold">{dashboardData.byTicketType?.incident?.ongoingTickets || 0}</span></span>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4">
                {/* Response SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Response SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.incident?.response?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.incident?.response?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.incident?.response?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.incident?.response?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.incident?.response?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.incident?.response?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.incident?.response?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.incident?.response?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESPONSE', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.incident?.response?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>

                {/* Resolution SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Resolution SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.incident?.resolution?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.incident?.resolution?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.incident?.resolution?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.incident?.resolution?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.incident?.resolution?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.incident?.resolution?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.incident?.resolution?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.incident?.resolution?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'RESOLUTION', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.incident?.resolution?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>

                {/* Ongoing SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Ongoing SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.incident?.ongoingSla?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.incident?.ongoingSla?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.incident?.ongoingSla?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.incident?.ongoingSla?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.incident?.ongoingSla?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.incident?.ongoingSla?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.incident?.ongoingSla?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.incident?.ongoingSla?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('INCIDENT', 'ONGOING', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.incident?.ongoingSla?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Request SLA Card - Full Width with 3 SLA Types */}
            <div className="bg-white rounded-lg shadow xl:col-span-3">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Service Request SLA</h3>
              </div>
              {/* Unique Ticket Summary - avoids double-counting tickets with multiple SLA types */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 font-medium">Unique Tickets:</span>
                  <div className="flex gap-4">
                    <span className="text-gray-700">Total: <span className="font-bold">{dashboardData.byTicketType?.serviceRequest?.totalTickets || 0}</span></span>
                    <span className="text-green-600">Met: <span className="font-bold">{dashboardData.byTicketType?.serviceRequest?.metTickets || 0}</span></span>
                    <span className="text-red-600">Breached: <span className="font-bold">{dashboardData.byTicketType?.serviceRequest?.breachedTickets || 0}</span></span>
                    <span className="text-blue-600">Ongoing: <span className="font-bold">{dashboardData.byTicketType?.serviceRequest?.ongoingTickets || 0}</span></span>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4">
                {/* Response SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Response SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.serviceRequest?.response?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.serviceRequest?.response?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.serviceRequest?.response?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.serviceRequest?.response?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.serviceRequest?.response?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.serviceRequest?.response?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.serviceRequest?.response?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.serviceRequest?.response?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESPONSE', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.serviceRequest?.response?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>

                {/* Resolution SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Resolution SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.serviceRequest?.resolution?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.serviceRequest?.resolution?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.serviceRequest?.resolution?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'RESOLUTION', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.serviceRequest?.resolution?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>

                {/* Ongoing SLA */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Ongoing SLA</span>
                    <span className="text-sm text-gray-500">
                      Compliance: {dashboardData.byTicketType?.serviceRequest?.ongoingSla?.compliance || 0}%
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                    <div className="p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'all')}>
                      <div className="text-xl font-bold text-gray-700">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded cursor-pointer hover:bg-green-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'met')}>
                      <div className="text-xl font-bold text-green-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.met || 0}</div>
                      <div className="text-xs text-gray-500">Met</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded cursor-pointer hover:bg-red-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'breached')}>
                      <div className="text-xl font-bold text-red-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.breached || 0}</div>
                      <div className="text-xs text-gray-500">Breached</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'ongoing')}>
                      <div className="text-xl font-bold text-blue-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.ongoing || 0}</div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'paused')}>
                      <div className="text-xl font-bold text-indigo-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.paused || 0}</div>
                      <div className="text-xs text-gray-500">Paused</div>
                    </div>
                    <div className="p-3 bg-gray-100 rounded cursor-pointer hover:bg-gray-200" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'cancelled')}>
                      <div className="text-xl font-bold text-gray-500">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.cancelled || 0}</div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'warning')}>
                      <div className="text-xl font-bold text-yellow-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.warning || 0}</div>
                      <div className="text-xs text-gray-500">Warning</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleTicketTypeSlaClick('SERVICE_REQUEST', 'ONGOING', 'atRisk')}>
                      <div className="text-xl font-bold text-orange-600">{dashboardData.byTicketType?.serviceRequest?.ongoingSla?.atRisk || 0}</div>
                      <div className="text-xs text-gray-500">At Risk</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtered Tickets Table */}
          <UnifiedPulseReportTable
            visible={tableVisible}
            filterLabel={tableFilterLabel}
            baseFilters={{
              customerId,
              timeRange,
              assignmentGroupId,
              timezoneOffset,
            }}
            chartFilters={{
              // SLA Monitoring dashboard shows CASE, INCIDENT, and SERVICE_REQUEST
              // Use specific type if set, otherwise default to all three types
              ticketType: chartFilters.ticketType || 'CASE,INCIDENT,SERVICE_REQUEST',
              filterType: chartFilters.filterType,
              filterValue: chartFilters.filterValue || chartFilters.slaType,
            }}
            onClose={closeTable}
            customerName={selectedCustomerName}
          />
        </>
      )}
    </div>
  );
};

export default SlaMonitoringDashboard;
